import { ChevronDown, Search } from "lucide-react";
import { useRef, useState } from "react";
import { JobTypeChip } from "@/components/ui/JobTypeChip";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useWorkOrders, type WorkOrder } from "@/hooks/use-work-orders";

interface WorkOrderComboboxProps {
  value: WorkOrder | null;
  onChange: (wo: WorkOrder | null) => void;
}

export function WorkOrderCombobox({ value, onChange }: WorkOrderComboboxProps) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 250);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useWorkOrders({
    search: debouncedQuery || undefined,
    status: "active",
    limit: 50,
  });

  const options = data?.data ?? [];

  function select(wo: WorkOrder) {
    onChange(wo);
    setOpen(false);
    setQuery("");
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const wo = options[activeIdx];
      if (wo) select(wo);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  if (value) {
    return (
      <div>
        <div className="wo-selected">
          <div className="row-center grow">
            <span className="wo-selected__wo">{value.woNumber}</span>
            <span className="wo-selected__client">{value.client}</span>
          </div>
          <button type="button" className="wo-selected__change" onClick={() => onChange(null)}>
            Change WO
          </button>
        </div>
        <div className="meta-chips">
          <span className="meta-chip">
            <strong>Client</strong> {value.client}
          </span>
          <JobTypeChip type={value.jobType} />
        </div>
      </div>
    );
  }

  return (
    <div className="combobox">
      <div style={{ position: "relative" }}>
        <Search className="combobox__search-icon" size={16} />
        <input
          ref={inputRef}
          type="text"
          className="form-input combobox__input"
          placeholder="Search by WO number or client…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIdx(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        <button
          type="button"
          className="combobox__caret"
          aria-label={open ? "Close work order list" : "Open work order list"}
          onMouseDown={(e) => {
            e.preventDefault();
            setOpen((o) => !o);
            inputRef.current?.focus();
          }}
        >
          <ChevronDown size={16} />
        </button>
      </div>

      {open && (
        <div className="combobox__menu">
          {isLoading ? (
            <div className="combobox__loading">
              <div className="skeleton-row" />
              <div className="skeleton-row" />
              <div className="skeleton-row" />
            </div>
          ) : options.length === 0 ? (
            <div className="combobox__empty">
              <Search size={22} />
              <span>
                {debouncedQuery
                  ? `No active work orders match "${debouncedQuery}"`
                  : "No active work orders"}
              </span>
              <span style={{ fontSize: 11, color: "var(--muted-2)" }}>
                Contact an admin to add new work orders
              </span>
            </div>
          ) : (
            options.map((wo, i) => (
              <div
                key={wo.id}
                className={`combobox__option${i === activeIdx ? " is-active" : ""}`}
                onMouseDown={() => select(wo)}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <div className="combobox__option-main">
                  <span className="combobox__option-wo">{wo.woNumber}</span>
                  <span className="combobox__option-client">{wo.client}</span>
                </div>
                <JobTypeChip type={wo.jobType} />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
